import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { downloadPdfReport } from '../tools/scripts/downloadPdfReport';

const PENDING_KEY = 'pendingDownload';
const PENDING_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

function isSafeRelativePath(path) {
    return typeof path === 'string'
        && path.startsWith('/')
        && !path.startsWith('//');
}


class DownloadReportPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            status: 'idle', // idle | downloading | success | error
            errorMessage: '',
            processId: null,
        };
    }

    componentDidMount() {
        const params = new URLSearchParams(this.props.location.search);
        const processId = params.get('process_id');

        if (!processId) {
            this.setState({
                status: 'error',
                errorMessage: 'Falta el identificador del proceso en el enlace.',
            });
            return;
        }

        this.setState({ processId });

        const token = this.getAuthToken();
        if (!token) {
            this.redirectToLogin(processId);
            return;
        }

        this.startDownload(processId, token);
    }

    getAuthToken() {
        // Redux store populated on login (existing pattern in the app)
        const activeUser = this.props.activeUser;
        return activeUser && activeUser.authToken ? activeUser.authToken : null;
    }

    redirectToLogin(processId) {
        try {
            localStorage.setItem(
                PENDING_KEY,
                JSON.stringify({ processId, timestamp: Date.now() })
            );
        } catch (_) { /* localStorage may be unavailable */ }

        const nextPath = '/auth/download-report?process_id=' + encodeURIComponent(processId);
        this.props.history.push(
            '/auth/login?next=' + encodeURIComponent(nextPath)
        );
    }

    async startDownload(processId, token) {
        this.setState({ status: 'downloading' });

        const result = await downloadPdfReport(processId, token);

        if (result.ok) {
            try { localStorage.removeItem(PENDING_KEY); } catch (_) {}
            this.setState({ status: 'success' });
            return;
        }

        if (result.status === 401) {
            this.redirectToLogin(processId);
            return;
        }

        let message = result.error || 'No se pudo descargar el reporte.';
        if (result.status === 404) {
            message = 'El reporte no está disponible para este proceso.';
        } else if (result.status === 400) {
            message = result.error || 'El proceso no tiene un reporte listo.';
        } else if (result.status === 502) {
            message = 'El bot externo no respondió. Intenta de nuevo en un momento.';
        } else if (result.status === 403) {
            message = 'No tienes permiso para descargar este reporte.';
        }

        this.setState({ status: 'error', errorMessage: message });
    }

    goHome = () => {
        this.props.history.push('/');
    };

    retry = () => {
        const token = this.getAuthToken();
        if (token && this.state.processId) {
            this.startDownload(this.state.processId, token);
        }
    };

    render() {
        const { status, errorMessage } = this.state;

        const cardStyle = {
            maxWidth: 420,
            margin: '80px auto',
            background: '#ffffff',
            padding: '40px 30px',
            borderRadius: 8,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            textAlign: 'center',
            fontFamily: 'Arial, Helvetica, sans-serif',
        };
        const iconStyle = { fontSize: 48, lineHeight: 1, marginBottom: 12 };
        const buttonStyle = {
            display: 'inline-block',
            padding: '10px 24px',
            background: '#1e8e8e',
            color: '#ffffff',
            textDecoration: 'none',
            borderRadius: 6,
            fontWeight: 'bold',
            fontSize: 13,
            border: 'none',
            cursor: 'pointer',
            marginTop: 16,
        };

        if (status === 'downloading' || status === 'idle') {
            return (
                <div style={cardStyle}>
                    <div style={iconStyle}>⏳</div>
                    <h2 style={{ fontSize: 16, color: '#2C3E50' }}>
                        Preparando tu descarga…
                    </h2>
                    <p style={{ color: '#888', fontSize: 13 }}>
                        Esto puede tardar unos segundos.
                    </p>
                </div>
            );
        }

        if (status === 'success') {
            return (
                <div style={cardStyle}>
                    <div style={{ ...iconStyle, color: '#28a745' }}>✓</div>
                    <h2 style={{ fontSize: 16, color: '#2C3E50' }}>
                        Descarga iniciada
                    </h2>
                    <p style={{ color: '#888', fontSize: 13 }}>
                        El PDF debería aparecer en tu navegador en segundos.
                    </p>
                    <button style={buttonStyle} onClick={this.goHome}>
                        Volver al dashboard
                    </button>
                </div>
            );
        }

        // status === 'error'
        return (
            <div style={cardStyle}>
                <div style={{ ...iconStyle, color: '#dc3545' }}>✗</div>
                <h2 style={{ fontSize: 16, color: '#2C3E50' }}>
                    No se pudo descargar
                </h2>
                <p style={{ color: '#666', fontSize: 13 }}>{errorMessage}</p>
                <div>
                    <button style={buttonStyle} onClick={this.retry}>
                        Reintentar
                    </button>
                    <button
                        style={{ ...buttonStyle, background: '#888', marginLeft: 8 }}
                        onClick={this.goHome}
                    >
                        Volver al dashboard
                    </button>
                </div>
            </div>
        );
    }
}


const mapStateToProps = function (state) {
    return { activeUser: state.mainReducer.activeUser };
};

export default withRouter(connect(mapStateToProps)(DownloadReportPage));

// Exported for the LoginForm to reuse (keeps validation in one place).
export { isSafeRelativePath, PENDING_KEY, PENDING_MAX_AGE_MS };
